import { useEffect, useRef } from "preact/hooks";
import { PageData } from "../src/pages.tsx";
import { useSignal } from "@preact/signals";

/* util: “Network Infrastructure” → “network-infrastructure” */
const slugify = (s: string) => s.trim().toLowerCase().replace(/\s+/g, "-");

export function PageSlider({ pages }: { pages: PageData[] }) {
  /* SSR default = first slide */
  const activeIndex = useSignal(0);

  /* refs */
  const contentRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const obsRef = useRef<IntersectionObserver>();

  /* dynamic height */
  const containerHeight = useSignal(0);

  /* 1. On client mount, pick initial slide from ?tab= and scroll there */
  useEffect(() => {
    const qsSlug = slugify(
      new URL(globalThis.location.href).searchParams.get("tab") ?? "",
    );
    const idx = pages.findIndex((p) => slugify(p.short) === qsSlug);
    const init = idx >= 0 ? idx : 0;
    activeIndex.value = init;
    scrollTo(init, false); // no smooth scroll on first load
  }, []);

  /* 2. IntersectionObserver keeps activeIndex in sync while scrolling */
  useEffect(() => {
    if (!contentRef.current) return;

    obsRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = Array.from(contentRef.current!.children).indexOf(
              e.target,
            );
            if (idx !== -1) activeIndex.value = idx;
          }
        });
      },
      { root: contentRef.current.parentElement, threshold: 0.5 },
    );

    Array.from(contentRef.current.children).forEach((el) =>
      obsRef.current?.observe(el)
    );

    return () => obsRef.current?.disconnect();
  }, []);

  /* 3. Move underline indicator */
  useEffect(() => {
    if (!buttonsRef.current || !lineRef.current) return;
    const btn = buttonsRef.current.children[activeIndex.value + 1] as HTMLElement; // +1 skips .indicator
    if (!btn) return;
    lineRef.current.style.width = `${btn.offsetWidth}px`;
    lineRef.current.style.transform = `translateX(${btn.offsetLeft}px)`;
  }, [activeIndex.value]);

  /* 4. Write ?tab= back to the URL */
  useEffect(() => {
    if (typeof document === "undefined") return;
    const slug = slugify(pages[activeIndex.value]?.short ?? "");
    const url = new URL(globalThis.location.href);
    url.searchParams.set("tab", slug);
    history.replaceState(null, "", url);
  }, [activeIndex.value]);

  /* 5. Adjust height based on active page */
  useEffect(() => {
    if (typeof document === "undefined") return;
    const container = contentRef.current;
    if (!container) return;
    const activeChild = container.children[activeIndex.value] as HTMLElement | undefined;
    if (activeChild) {
      containerHeight.value = activeChild.offsetHeight;
    }
  }, [activeIndex.value]);

  /* helper */
  const scrollTo = (idx: number, smooth = true) => {
    if (!contentRef.current) return;
    const section = contentRef.current.children[idx] as HTMLElement;
    contentRef.current.parentElement?.scrollTo({
      left: section.offsetLeft,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  /* render */
  return (
    <div class="page-slider">
      <div class="buttons" ref={buttonsRef}>
        <div class="indicator" ref={lineRef} />
        {pages.map((p, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => scrollTo(idx)}
            class={activeIndex.value === idx ? "active" : ""}
          >
            {p.short}
          </button>
        ))}
      </div>

      <div class="scroll" style={{ height: `${containerHeight.value}px` }}>
        <div class="content" ref={contentRef}>
          {pages.map((p, idx) => (
            <div key={idx}>
              <div
                class="centered"
                dangerouslySetInnerHTML={{ __html: p.html }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
